<!-- To use, add class:"peer" to sibling component and class:"relative" to parent component -->

<script setup lang="ts">
const props = defineProps({
  position: {
    default: "top",
    validator: (value: string) => {
      return ["top", "right", "bottom", "left", "custom"].includes(value);
    },
  },
  isVisible: {
    type: Boolean,
    required: false,
    default: false,
  },
  customClass: {
    type: String,
    required: false,
    default: undefined,
  },
  customArrowClass: {
    type: String,
    required: false,
    default: undefined,
  },
  customStyle: {
    type: Object,
    required: false,
    default: undefined,
  },
  customArrowStyle: {
    type: Object,
    required: false,
    default: undefined,
  },
  disableShift: {
    type: Boolean,
    required: false,
    default: false,
  },
});

//wip: fix all positions besides default/top. need to move arrow too
const tooltipRef = ref<HTMLElement | null>(null);
const shiftX = ref(0);
let triggerElement: HTMLElement | null = null;

const extraClasses = computed(() => {
  switch (props.position) {
    case "top":
      return ["translate-x-1/2 right-1/2 bottom-[140%]"];
    case "left":
      return ["-translate-x-6 right-1/2 -top-1"];
    case "bottom":
      return ["translate-x-1/2 right-1/2 top-[140%]"];
    case "right":
      return ["translate-x-6 left-1/2 -top-2"];
    case "custom":
      return props.customClass ? props.customClass : undefined;
    default:
      return ["translate-x-1/2 right-1/2 -top-12"];
  }
});
const extraArrowClasses = computed(() => {
  switch (props.position) {
    case "top":
      return ["left-1/2 top-full -translate-x-1/2"];
    case "left":
      return ["left-full top-1/2 -translate-y-1/2 -rotate-90"];
    case "bottom":
      return ["bottom-full left-1/2 -translate-x-1/2 rotate-180"];
    case "right":
      return ["right-full top-1/2 -translate-y-1/2 rotate-90"];
    case "custom":
      return props.customArrowClass ? props.customArrowClass : undefined;
    default:
      return ["left-1/2 top-full -translate-x-1/2"];
  }
});

const isTooltipVisible = () => {
  const tooltip = tooltipRef.value;
  if (!tooltip) {
    return false;
  }
  const style = window.getComputedStyle(tooltip);
  return style.display !== "none" && style.visibility !== "hidden";
};

const updateShift = () => {
  const tooltip = tooltipRef.value;
  if (!tooltip) {
    return;
  }
  if (props.disableShift) {
    return;
  }
  if (!isTooltipVisible()) {
    return;
  }
  const rect = tooltip.getBoundingClientRect();
  const viewportPadding = 8;
  let nextShift = 0;

  if (rect.left < viewportPadding) {
    nextShift = viewportPadding - rect.left;
  } else if (rect.right > window.innerWidth - viewportPadding) {
    nextShift = window.innerWidth - viewportPadding - rect.right;
  }

  shiftX.value = nextShift;
};

const shiftStyle = computed(() => {
  return {
    marginLeft: `${shiftX.value}px`,
  };
});

const scheduleShift = () => {
  requestAnimationFrame(updateShift);
};

const resetShift = () => {
  shiftX.value = 0;
};

watch(
  () => props.isVisible,
  (visible) => {
    if (props.disableShift) {
      return;
    }
    if (!visible) {
      resetShift();
      return;
    }
    nextTick(() => {
      scheduleShift();
    });
  }
);

onMounted(() => {
  const tooltip = tooltipRef.value;
  triggerElement = (tooltip?.previousElementSibling as HTMLElement | null) ?? null;
  if (triggerElement && !props.disableShift) {
    triggerElement.addEventListener("mouseenter", scheduleShift);
    triggerElement.addEventListener("focus", scheduleShift);
    triggerElement.addEventListener("mouseleave", resetShift);
    triggerElement.addEventListener("blur", resetShift);
  }
  if (!props.disableShift) {
    window.addEventListener("resize", scheduleShift);
    window.addEventListener("scroll", scheduleShift, true);
  }
  if (props.isVisible && !props.disableShift) {
    nextTick(() => {
      scheduleShift();
    });
  }
});

onBeforeUnmount(() => {
  if (triggerElement && !props.disableShift) {
    triggerElement.removeEventListener("mouseenter", scheduleShift);
    triggerElement.removeEventListener("focus", scheduleShift);
    triggerElement.removeEventListener("mouseleave", resetShift);
    triggerElement.removeEventListener("blur", resetShift);
  }
  if (!props.disableShift) {
    window.removeEventListener("resize", scheduleShift);
    window.removeEventListener("scroll", scheduleShift, true);
  }
});

defineExpose({
  tooltipRef,
});
</script>

<template>
  <div
    :class="
      [
        'pointer-events-none absolute w-fit max-w-[calc(100vw-2rem)] p-2 bg-neutral-200 flex-col justify-center items-center flex-nowrap rounded-sm z-10',
        extraClasses,
        props.isVisible ? 'flex' : 'hidden peer-hover:flex',
      ]
    "
    :style="[shiftStyle, props.customStyle]"
    ref="tooltipRef">
    <slot name="tooltip-text" />
    <div
      class="pointer-events-none absolute h-0 w-0 border-solid border-neutral-200 border-t-8 border-x-8 border-b-0 border-x-transparent"
      :class="extraArrowClasses"
      :style="props.customArrowStyle" />
  </div>
</template>
